<script setup>
import { computed } from 'vue'
import { RotateCcw } from '@lucide/vue'
import { Badge, Button, Table } from '@drama/ui'

import AuditId from './AuditId.vue'
import { clockTime, fullTime, jobRange, useAuditText, variantOf } from './audit-text.js'
import { asArray } from './run-observer.js'

const props = defineProps({
  branch: { type: Object, required: true },
  // 正在重试的任务 id；整批重试时为 all，空串表示没有。
  retrying: { type: String, default: '' },
})
const emit = defineEmits(['retry'])

const { tr, enumLabel, errorLabel } = useAuditText()
const jobs = computed(() => asArray(props.branch.jobs))
const columns = computed(() => [
  { key: 'kind', label: tr('jobs.kind'), width: '240px' },
  { key: 'range', label: tr('jobs.range'), width: '110px', mono: true },
  { key: 'status', label: tr('jobs.status'), width: '120px' },
  { key: 'attempts', label: tr('jobs.attempts'), width: '90px', align: 'right', mono: true },
  { key: 'next_retry_at', label: tr('jobs.nextRetry'), width: '170px' },
  { key: 'error', label: tr('jobs.error'), width: '320px', wrap: true },
  { key: 'retry', label: tr('jobs.retry'), width: '120px' },
])

/** 触发原因与完成时间收进类型列的 title。 */
function jobTitle(job) {
  const parts = []
  if (job.trigger) parts.push(tr('jobs.trigger', { trigger: enumLabel('jobs.triggers', job.trigger) }))
  if (job.completed_at) parts.push(tr('jobs.completed', { time: fullTime(job.completed_at) }))
  return parts.join(' · ') || undefined
}
</script>

<template>
  <Table v-if="jobs.length" :columns="columns" :rows="jobs" row-key="job_id" data-testid="observer-jobs">
    <template #kind="{ row }">
      <span :title="jobTitle(row)">{{ enumLabel('jobs.kinds', row.kind || 'memory') }}</span>
      <AuditId :id="row.job_id" />
    </template>
    <template #range="{ row }">{{ jobRange(row) }}</template>
    <template #status="{ row }"><Badge size="sm" :variant="variantOf(row.status)">{{ enumLabel('jobs.statuses', row.status) }}</Badge></template>
    <template #attempts="{ row }">{{ row.attempts }}</template>
    <template #next_retry_at="{ row }">
      <span v-if="row.next_retry_at" :title="fullTime(row.next_retry_at)">{{ clockTime(row.next_retry_at) }}</span>
      <span v-else class="au-muted">—</span>
    </template>
    <template #error="{ row }">
      <span v-if="row.error_code" class="au-error" :title="row.error_code">{{ errorLabel(row.error_code) }}</span>
      <span v-else class="au-muted">—</span>
    </template>
    <template #retry="{ row }">
      <Button
        v-if="row.status === 'failed'"
        variant="secondary"
        size="sm"
        :loading="retrying === row.job_id"
        :disabled="retrying !== ''"
        @click="emit('retry', row)"
      >
        <RotateCcw :size="14" aria-hidden="true" /> {{ tr('jobs.retry') }}
      </Button>
      <span v-else class="au-muted">—</span>
    </template>
  </Table>
  <p v-else class="au-empty">{{ tr('empty') }}</p>
</template>
